/**
 * Seller — "Mis pedidos" page.
 *
 * Read-only list of the orders the current seller created. Each row shows
 * customer + total + date + a WhatsApp share button. Delete-own is allowed.
 */
import { useEffect, useState } from "react";
import { api } from "../../App";
import { toast } from "sonner";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { formatMoney } from "../../lib/money";
import { MessageCircle, Trash2, ShoppingCart, Calendar } from "lucide-react";
import { EmptyState } from "../../components/EmptyState";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";

export default function SellerOrdersList() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [delTarget, setDelTarget] = useState(null);

  const fetchOrders = async () => {
    try {
      const { data } = await api.get("/seller/orders");
      setOrders(data);
    } catch {
      toast.error("Error al cargar tus pedidos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrders(); }, []);

  const shareWhatsApp = (o) => {
    const lines = o.lineas
      .map((l) => `• ${l.codigo} x${l.cantidad} — ${formatMoney(l.subtotal)}`)
      .join("\n");
    const msg =
      `🛒 *Pedido*\n` +
      `Cliente: ${o.nombre_cliente}\n` +
      `Vendedor: ${o.nombre_vendedor}\n\n` +
      `${lines}\n\n` +
      `*Total: ${formatMoney(o.total)}*`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank", "noopener");
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/seller/orders/${delTarget.id}`);
      toast.success("Pedido eliminado");
      setDelTarget(null);
      fetchOrders();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  if (loading) return <div className="text-center py-12 text-slate-500">Cargando…</div>;

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
        Mis pedidos
      </h2>

      {orders.length === 0 ? (
        <EmptyState
          icon={ShoppingCart}
          title="Todavía no tomaste pedidos"
          description="Andá a Tomar pedido para crear el primero."
          testid="seller-orders-empty"
        />
      ) : (
        <div className="space-y-3">
          {orders.map((o) => (
            <Card key={o.id} className="bg-white border border-slate-200 rounded-sm p-4" data-testid={`seller-order-card-${o.id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-900 truncate">{o.nombre_cliente}</p>
                  <p className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {new Date(o.created_at).toLocaleDateString("es-CR", { day: "2-digit", month: "short", year: "numeric" })}
                  </p>
                </div>
                <span className="text-lg font-bold whitespace-nowrap" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
                  {formatMoney(o.total)}
                </span>
              </div>
              <div className="text-xs text-slate-600 mb-3">
                {o.lineas.length} {o.lineas.length === 1 ? "línea" : "líneas"}: {o.lineas.slice(0, 3).map((l) => `${l.codigo} ×${l.cantidad}`).join(" · ")}
                {o.lineas.length > 3 && ` · +${o.lineas.length - 3}`}
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => shareWhatsApp(o)}
                  className="flex-1 rounded-sm bg-[#25D366] hover:bg-[#1ebe5d] text-white border-0"
                  data-testid={`seller-share-${o.id}`}
                >
                  <MessageCircle className="h-3.5 w-3.5 mr-1.5" /> WhatsApp
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setDelTarget(o)}
                  className="rounded-sm text-red-600 border-red-200 hover:bg-red-50"
                  data-testid={`seller-delete-${o.id}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar pedido?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700 text-white">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
